
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Discrepancy } from "@/types/discrepancy";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface JustificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  discrepancy: Discrepancy | null;
  onSubmit: (explanation: string) => void;
}

export const JustificationDialog = ({
  open,
  onOpenChange,
  discrepancy,
  onSubmit,
}: JustificationDialogProps) => {

  const handleConfirm = async () => {
    if (!discrepancy) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Utilisateur non authentifié");
        return;
      }

      // Valide le montant dans validated_amounts
      const { error: validationError } = await supabase
        .from("validated_amounts")
        .insert({
          counting_date: discrepancy.counting_date,
          payment_method: discrepancy.payment_method,
          amount: discrepancy.counted_amount, // Utilise toujours le montant compté
          status: "validated",
          owner_id: user.id
        });

      if (validationError) {
        console.error("Error validating amount:", validationError);
        toast.error("Erreur lors de la validation du montant");
        return;
      }

      // Met à jour les transactions de volaille comme revenus validés
      const { error: transactionError } = await supabase
        .from("poultry_transactions")
        .update({ is_deducted_from_revenue: true })
        .eq("created_by", user.id)
        .eq("created_at", discrepancy.counting_date);

      if (transactionError) {
        console.error("Error updating transactions:", transactionError);
        toast.error("Erreur lors de la mise à jour des transactions");
        return;
      }

      const explanation = `Montant compté validé (${discrepancy.counted_amount.toLocaleString()} FCFA)`;
      
      onSubmit(explanation);
      toast.success("Montant validé avec succès");
    } catch (error) {
      console.error("Error in handleConfirm:", error);
      toast.error("Une erreur est survenue");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirmer la validation</DialogTitle>
        </DialogHeader>
        
        <div className="py-6">
          <div className="text-center space-y-2">
            <p className="font-medium">
              Le montant compté ({discrepancy?.counted_amount.toLocaleString()} FCFA) sera validé.
            </p>
            <p className="text-sm text-gray-600">
              Cette action ne peut pas être annulée.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleConfirm}>
            Confirmer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
